/**
 * SiteOverviewPage — the per-site live view.
 *
 * 7 sections stacked vertically:
 *   1. Site picker + range selector + refresh
 *   2. Three device-class roll-up cards (appliances/switches/wireless)
 *   3. Three stat tiles (WAN uplinks / active clients / peak throughput)
 *   4. Throughput area chart + appliance status rail (right column)
 *   5. Network topology tree
 *   6. Connected clients table (paginated, searchable)
 *   7. Devices table (paginated, sortable)
 *
 * Dashboard sections refresh on explicit user action.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Edge, Node } from "@xyflow/react";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  FileSpreadsheet,
  RefreshCw,
  Search,
  Users,
  Cable,
  Download,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  CardGridSkeleton,
  PanelSkeleton,
  TableSkeleton,
} from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MultiSelectDropdown,
  type MultiSelectOption,
} from "@/components/ui/multi-select-dropdown";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSites } from "@/stores/sites";
import { getLogger } from "@/lib/logging";
import { useAiExplain, type AiExplanation } from "@/stores/ai-explain";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import type {
  DeviceClassRollup,
  SiteDevice,
  ThroughputPoint,
} from "@/api/schemas/sites";
import { cn } from "@/lib/cn";
import { m } from "framer-motion";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";
import { SitePickerInline } from "@/components/app/dashboard/site-picker-inline";
import { RangePicker } from "@/components/app/dashboard/range-picker";
import { DeviceClassCard } from "@/components/app/dashboard/device-class-card";
import { StatTile } from "@/components/app/dashboard/stat-tile";
import { ThroughputChartSurface } from "@/components/app/dashboard/throughput-chart-surface";
import { ChartStat } from "@/components/app/dashboard/chart-stat";
import { ApplianceStatusRail } from "@/components/app/dashboard/appliance-status-rail";
import { DashboardAiSummaryPanel } from "@/components/app/dashboard/ai-summary-panel";
import { FirmwareUpgradesSection } from "@/components/app/dashboard/firmware-upgrades-section";
import { LicensesSection } from "@/components/app/dashboard/licenses-section";
import { AlertsSection } from "@/components/app/dashboard/alerts-section";
import { ConnectedClientsTable } from "@/components/app/dashboard/connected-clients-table";
import { DevicesTable } from "@/components/app/dashboard/devices-table";
import { SiteDiagram } from "@/components/app/site-topology-tree";
import { TopologyExplorer } from "@/components/app/topology/topology-explorer";
import {
  useTopologyLinkLayer,
  type LinkLayerTopology,
} from "@/stores/topology-linklayer";
import { useTopologyDeviceDetails } from "@/stores/topology-device-details";
import { useTheme } from "@/stores/theme";

/* ---------- formatters ---------- */

// Enhanced formatter for peak throughput
function fmtBps(bps: number): string {
  // If less than 1000, show as b/s
  if (bps < 1_000) return `${bps.toFixed(0)} b/s`;
  // If less than 1,000,000, show as Kb/s
  if (bps < 1_000_000) return `${(bps / 1_000).toFixed(0)} Kb/s`;
  // If less than 1,000,000,000, show as Mb/s, but if >= 1,000,000,000, show as Gb/s
  if (bps < 1_000_000_000) {
    const mbps = bps / 1_000_000;
    if (mbps < 1000) {
      // Show as Mb/s, max 1000 before summarizing
      return `${mbps % 1 === 0 ? mbps.toFixed(0) : mbps.toFixed(1)} Mb/s`;
    }
    // 1000 Mb/s = 1 Gb/s
    // fall through
  }
  // 1,000,000,000 bps = 1 Gb/s
  const gbps = bps / 1_000_000_000;
  if (gbps < 1000) {
    // Show as Gb/s, up to 1000
    return `${gbps % 1 === 0 ? gbps.toFixed(0) : gbps.toFixed(1)} Gb/s`;
  }
  // If over 1000 Gb/s, show as e.g. 1.1k Gb/s
  return `${(gbps / 1000).toFixed(1)}k Gb/s`;
}
function fmtBytes(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)} GB`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)} MB`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} KB`;
  return `${n} B`;
}
function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "less than a minute ago";
  if (ms < 3_600_000)
    return `${Math.floor(ms / 60_000)} minute${ms < 120_000 ? "" : "s"} ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

/** Format a _sectionFetchedAt millisecond timestamp as "refreshed X ago". */
function fmtFetchedAt(ms: number | undefined): string | null {
  if (!ms) return null;
  const age = Date.now() - ms;
  if (age < 60_000) return "refreshed less than a minute ago";
  if (age < 3_600_000) return `refreshed ${Math.floor(age / 60_000)}m ago`;
  return `refreshed ${Math.floor(age / 3_600_000)}h ago`;
}

const logger = getLogger("opus.dashboard");

/* ---------- range options ---------- */

const RANGE_OPTIONS = [
  { id: "1h", label: "Last 1 hour" },
  { id: "24h", label: "Last 24 hours" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
];

const CUSTOM_RANGE_PREFIX = "custom:";

const DEVICE_CLASS_OPTIONS: MultiSelectOption[] = [
  { value: "wan", label: "Internet WAN" },
  { value: "appliance", label: "Appliances" },
  { value: "switch", label: "Switches" },
  { value: "wireless", label: "Wireless" },
];

type TopologyDeviceClass = SiteDevice["deviceClass"] | "wan";

type TopologyDevice = Omit<SiteDevice, "deviceClass"> & {
  deviceClass: TopologyDeviceClass;
};

type NonWanDevice = SiteDevice & {
  deviceClass: Exclude<SiteDevice["deviceClass"], "wan">;
};

type DashboardSection =
  | "overview"
  | "throughput"
  | "topology"
  | "clients"
  | "devices"
  | "firmware"
  | "licenses"
  | "alerts"
  | "assuranceAlerts"
  | "wirelessFailedConnections";

type ThroughputGraph = "applianceUplinkBandwidth" | "clientBandwidthUsage";
type ThroughputSeriesKey = "Received" | "Sent";
type NetworkDevicesView = "topology" | "diagram" | "table";
type AlertsTab = "networkEvents" | "health" | "connections";

interface TopologyLinkEdgeData {
  sourcePort?: string | null;
  targetPort?: string | null;
  sourceProductType?: string | null;
  targetProductType?: string | null;
}

interface DashboardTopologyLayout {
  nodes: Node[];
  edges: Edge[];
  focusNodeId: string | null;
}

const AP_GROUP_PREFIX = "ap-group:";
const ISP_NODE_ID = "isp-root";

function classPriority(deviceClass: TopologyDeviceClass): number {
  if (deviceClass === "wan") return 0;
  if (deviceClass === "appliance") return 1;
  if (deviceClass === "switch") return 2;
  if (deviceClass === "wireless") return 3;
  return 4;
}

function buildDashboardLinkLayerLayout(
  topology: LinkLayerTopology,
  devicesBySerial: Map<string, TopologyDevice>,
  expandedApGroups: Set<string>,
): DashboardTopologyLayout {
  if (!topology.nodes.length) {
    return { nodes: [], edges: [], focusNodeId: null };
  }

  const nodeById = new Map(topology.nodes.map((n) => [n.id, n]));
  const idToDevice = new Map<string, TopologyDevice>();
  for (const node of topology.nodes) {
    if (!node.serial) continue;
    const device = devicesBySerial.get(node.serial);
    if (!device) continue;
    idToDevice.set(node.id, device);
  }
  if (!idToDevice.size) {
    return { nodes: [], edges: [], focusNodeId: null };
  }

  const adjacency = new Map<string, Set<string>>();
  for (const node of topology.nodes) adjacency.set(node.id, new Set());
  for (const link of topology.links) {
    if (!adjacency.has(link.source) || !adjacency.has(link.target)) continue;
    adjacency.get(link.source)?.add(link.target);
    adjacency.get(link.target)?.add(link.source);
  }

  const explicitRoot = topology.nodes.find((n) => n.isRoot);
  let rootId = explicitRoot?.id;
  if (!rootId) {
    const ranked = [...topology.nodes].sort((a, b) => {
      const aDevice = a.serial ? devicesBySerial.get(a.serial) : undefined;
      const bDevice = b.serial ? devicesBySerial.get(b.serial) : undefined;
      const ap = classPriority(aDevice?.deviceClass ?? "wireless");
      const bp = classPriority(bDevice?.deviceClass ?? "wireless");
      if (ap !== bp) return ap - bp;
      const da = adjacency.get(a.id)?.size ?? 0;
      const db = adjacency.get(b.id)?.size ?? 0;
      if (da !== db) return db - da;
      return a.name.localeCompare(b.name);
    });
    rootId = ranked[0]?.id;
  }
  if (!rootId) return { nodes: [], edges: [], focusNodeId: null };

  const depth = new Map<string, number>();
  const parent = new Map<string, string | null>();
  const order: string[] = [];
  const queue: string[] = [rootId];
  depth.set(rootId, 0);
  parent.set(rootId, null);

  while (queue.length > 0) {
    const current = queue.shift() as string;
    order.push(current);
    const currentDepth = depth.get(current) ?? 0;
    const neighbors = [...(adjacency.get(current) ?? [])].sort();
    for (const neighbor of neighbors) {
      if (depth.has(neighbor)) continue;
      depth.set(neighbor, currentDepth + 1);
      parent.set(neighbor, current);
      queue.push(neighbor);
    }
  }

  for (const node of topology.nodes) {
    if (depth.has(node.id)) continue;
    depth.set(node.id, 0);
    parent.set(node.id, null);
    order.push(node.id);
  }

  const totalManaged = idToDevice.size;
  const shouldGroupApNodes = totalManaged > 60;

  type EffectiveNode = {
    id: string;
    name: string;
    source: "device" | "ap-group";
    device: TopologyDevice;
    productType: string | null;
    depth: number;
    parentId: string | null;
    memberIds?: string[];
  };

  const effectiveNodes: EffectiveNode[] = [];
  const groupedApByParent = new Map<string, string[]>();

  for (const node of topology.nodes) {
    const device = idToDevice.get(node.id);
    if (!device) continue;
    const parentId = parent.get(node.id) ?? null;
    const isWireless = device.deviceClass === "wireless";
    const canGroup = shouldGroupApNodes && isWireless;
    if (canGroup) {
      const groupKey = `${AP_GROUP_PREFIX}${parentId ?? "root"}`;
      if (!expandedApGroups.has(groupKey)) {
        const bucket = groupedApByParent.get(groupKey) ?? [];
        bucket.push(node.id);
        groupedApByParent.set(groupKey, bucket);
        continue;
      }
    }
    effectiveNodes.push({
      id: node.id,
      name: node.name,
      source: "device",
      device,
      productType: node.productType,
      depth: depth.get(node.id) ?? 0,
      parentId,
    });
  }

  for (const [groupId, members] of groupedApByParent.entries()) {
    if (!members.length) continue;
    const parentId = parent.get(members[0] ?? "") ?? null;
    const memberDevices = members
      .map((id) => idToDevice.get(id))
      .filter((d): d is TopologyDevice => Boolean(d));
    if (!memberDevices.length) continue;
    const worstStatus = memberDevices.some((d) => d.status === "offline")
      ? "offline"
      : memberDevices.some((d) => d.status === "alerting")
        ? "alerting"
        : memberDevices.some((d) => d.status === "dormant")
          ? "dormant"
          : "online";
    const groupDevice: TopologyDevice = {
      serial: groupId,
      name: `Access Points (${memberDevices.length})`,
      model: "AP Group",
      macAddress: null,
      address: null,
      tags: [],
      notes: "Click to expand AP group",
      status: worstStatus,
      lanIp: null,
      publicIp: null,
      lastSeen: null,
      deviceClass: "wireless",
    };
    effectiveNodes.push({
      id: groupId,
      name: groupDevice.name,
      source: "ap-group",
      device: groupDevice,
      productType: "wireless",
      depth: (parentId ? (depth.get(parentId) ?? 0) : 0) + 1,
      parentId,
      memberIds: members,
    });
  }

  const byDepth = new Map<number, EffectiveNode[]>();
  for (const node of effectiveNodes) {
    const d = node.depth;
    const bucket = byDepth.get(d) ?? [];
    bucket.push(node);
    byDepth.set(d, bucket);
  }

  const depths = [...byDepth.keys()].sort((a, b) => a - b);
  const maxDepth = depths.length === 0 ? 0 : (depths[depths.length - 1] ?? 0);
  const maxColNodes = Math.max(
    ...depths.map((d) => byDepth.get(d)?.length ?? 0),
    1,
  );
  const density = Math.min(1, Math.max(0, (maxColNodes - 16) / 40));
  const COL_W = Math.round(320 + density * 140);
  const BRANCH_ROW_H = Math.round(86 + density * 28);
  const LEAF_ROW_H = Math.round(34 + density * 16);
  const COMPACT_THRESHOLD = Math.max(14, Math.round(24 - density * 8));
  const WRAP_THRESHOLD = Math.max(12, Math.round(24 - density * 10));

  type ColMeta = {
    compact: boolean;
    subCols: number;
    rowH: number;
    xOffset: number;
  };
  const colMeta = new Map<number, ColMeta>();
  let xCursor = 0;
  for (const d of depths) {
    const count = byDepth.get(d)?.length ?? 0;
    const compact = d === maxDepth || count > COMPACT_THRESHOLD;
    const subCols =
      count > WRAP_THRESHOLD ? Math.ceil(count / WRAP_THRESHOLD) : 1;
    const rowH = compact ? LEAF_ROW_H : BRANCH_ROW_H;
    colMeta.set(d, { compact, subCols, rowH, xOffset: xCursor });
    xCursor += subCols;
  }

  const nodes: Node[] = [];
  const includedIds = new Set<string>();
  const orderIndex = new Map(order.map((id, i) => [id, i]));

  for (const d of depths) {
    const bucket = (byDepth.get(d) ?? []).sort((a, b) => {
      const pa = a.parentId;
      const pb = b.parentId;
      const pai = pa ? (orderIndex.get(pa) ?? -1) : -1;
      const pbi = pb ? (orderIndex.get(pb) ?? -1) : -1;
      if (pai !== pbi) return pai - pbi;
      return a.name.localeCompare(b.name);
    });

    const meta = colMeta.get(d);
    const perSub =
      !meta || meta.subCols === 1
        ? bucket.length
        : Math.ceil(bucket.length / meta.subCols);
    const totalH = Math.max(0, (perSub - 1) * (meta?.rowH ?? BRANCH_ROW_H));
    bucket.forEach((node, idx) => {
      const device = node.device;
      includedIds.add(node.id);
      const subColIdx =
        meta?.subCols && meta.subCols > 1 ? Math.floor(idx / perSub) : 0;
      const rowInSub = meta?.subCols && meta.subCols > 1 ? idx % perSub : idx;
      const x = ((meta?.xOffset ?? 0) + subColIdx) * COL_W;
      const y = rowInSub * (meta?.rowH ?? BRANCH_ROW_H) - totalH / 2;
      nodes.push({
        id: node.id,
        type: "default",
        position: { x, y },
        data: {
          label: `${device.name} · ${device.status}`,
          device,
          source: node.source,
          memberCount: node.memberIds?.length ?? 0,
        },
      });
    });
  }

  const byX = new Map<number, Node[]>();
  for (const node of nodes) {
    const col = byX.get(node.position.x) ?? [];
    col.push(node);
    byX.set(node.position.x, col);
  }
  for (const col of byX.values()) {
    const sorted = [...col].sort((a, b) => a.position.y - b.position.y);
    const minGap = Math.round(60 + density * 22);
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = sorted[i - 1] as Node;
      const current = sorted[i] as Node;
      if (current.position.y - prev.position.y < minGap) {
        current.position.y = prev.position.y + minGap;
      }
    }
    const meanY =
      sorted.reduce((sum, item) => sum + item.position.y, 0) /
      Math.max(sorted.length, 1);
    sorted.forEach((item) => {
      item.position.y -= meanY;
    });
  }

  const sevRank = (status: SiteDevice["status"]) => {
    if (status === "offline") return 3;
    if (status === "alerting") return 2;
    if (status === "online") return 1;
    return 0;
  };

  const edgeSeverityStyle = (worst: number) => ({
    stroke:
      worst === 3
        ? "rgb(var(--rose))"
        : worst === 2
          ? "rgb(var(--peach))"
          : "rgb(var(--violet))",
    strokeOpacity: worst === 3 ? 0.9 : worst === 2 ? 0.7 : 0.6,
    strokeWidth: worst === 3 ? 2 : 1.5,
  });

  const effectiveNodeMap = new Map(effectiveNodes.map((n) => [n.id, n]));

  const edges: Edge[] = topology.links.flatMap((link, idx) => {
    if (!includedIds.has(link.source) || !includedIds.has(link.target)) {
      return [];
    }
    const ds = depth.get(link.source) ?? 0;
    const dt = depth.get(link.target) ?? 0;
    const [source, target] =
      ds <= dt ? [link.source, link.target] : [link.target, link.source];

    const sourceDevice = idToDevice.get(source);
    const targetDevice = idToDevice.get(target);
    const worst = Math.max(
      sevRank(sourceDevice?.status ?? "dormant"),
      sevRank(targetDevice?.status ?? "dormant"),
    );
    const sourceNode = nodeById.get(source);
    const targetNode = nodeById.get(target);

    return [
      {
        id: `ll-${source}-${target}-${idx}`,
        source,
        target,
        type: "smoothstep",
        animated: worst === 3,
        data: {
          sourcePort: ds <= dt ? link.sourcePort : link.targetPort,
          targetPort: ds <= dt ? link.targetPort : link.sourcePort,
          sourceProductType: sourceNode?.productType ?? null,
          targetProductType: targetNode?.productType ?? null,
        } satisfies TopologyLinkEdgeData,
        style: edgeSeverityStyle(worst),
      },
    ];
  });

  for (const [groupId, memberIds] of groupedApByParent.entries()) {
    if (!includedIds.has(groupId) || memberIds.length === 0) continue;
    const parentId = parent.get(memberIds[0] ?? "") ?? null;
    if (!parentId || !includedIds.has(parentId)) continue;
    const worst = Math.max(
      ...memberIds.map((id) => sevRank(idToDevice.get(id)?.status ?? "dormant")),
    );
    edges.push({
      id: `group-${parentId}-${groupId}`,
      source: parentId,
      target: groupId,
      type: "smoothstep",
      data: {
        sourcePort: null,
        targetPort: null,
        sourceProductType: effectiveNodeMap.get(parentId)?.productType ?? null,
        targetProductType: "wireless",
      } satisfies TopologyLinkEdgeData,
      animated: worst === 3,
      style: edgeSeverityStyle(worst),
    });
  }

  const rootNodePosition = nodes.find((n) => n.id === rootId)?.position ?? {
    x: 0,
    y: 0,
  };
  const ispDevice: TopologyDevice = {
    serial: ISP_NODE_ID,
    name: "ISP",
    model: "Internet",
    macAddress: null,
    address: null,
    tags: [],
    notes: null,
    status: "online",
    lanIp: null,
    publicIp: null,
    lastSeen: null,
    deviceClass: "wan",
  };
  nodes.push({
    id: ISP_NODE_ID,
    type: "default",
    position: {
      x: rootNodePosition.x - COL_W,
      y: rootNodePosition.y,
    },
    data: {
      label: "ISP · online",
      device: ispDevice,
      source: "isp",
    },
  });
  edges.push({
    id: `${ISP_NODE_ID}-${rootId}`,
    source: ISP_NODE_ID,
    target: rootId,
    type: "smoothstep",
    data: {
      sourcePort: null,
      targetPort: null,
      sourceProductType: "wan",
      targetProductType: nodeById.get(rootId)?.productType ?? null,
    } satisfies TopologyLinkEdgeData,
    style: {
      stroke: "rgb(var(--cyan))",
      strokeOpacity: 0.8,
      strokeWidth: 2,
    },
  });

  return { nodes, edges, focusNodeId: rootId };
}

function rangeToMs(rangeId: string): number {
  if (rangeId === "1h") return 60 * 60 * 1000;
  if (rangeId === "24h") return 24 * 60 * 60 * 1000;
  if (rangeId === "7d") return 7 * 24 * 60 * 60 * 1000;
  return 30 * 24 * 60 * 60 * 1000;
}

function parseCustomRange(
  rangeId: string,
): { from: number; to: number } | null {
  if (!rangeId.startsWith(CUSTOM_RANGE_PREFIX)) return null;
  const [fromDate, toDate] = rangeId
    .slice(CUSTOM_RANGE_PREFIX.length)
    .split("|");
  if (!fromDate || !toDate) return null;
  const from = new Date(`${fromDate}T00:00:00`).getTime();
  const to = new Date(`${toDate}T23:59:59.999`).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || from > to) return null;
  return { from, to };
}

function rangeLabel(rangeId: string): string {
  const preset = RANGE_OPTIONS.find((r) => r.id === rangeId);
  if (preset) return preset.label;
  const custom = parseCustomRange(rangeId);
  if (!custom) return "Last 24 hours";
  const from = new Date(custom.from).toLocaleDateString();
  const to = new Date(custom.to).toLocaleDateString();
  return `Custom: ${from} - ${to}`;
}

function clientsQueryForRange(
  rangeId: string,
): Record<string, string | number | boolean | undefined | null> {
  const custom = parseCustomRange(rangeId);
  if (custom) {
    return {
      t0: new Date(custom.from).toISOString(),
      t1: new Date(custom.to).toISOString(),
    };
  }
  // Meraki supports up to 30 days for timespan-style queries.
  const timespan = Math.min(
    Math.floor(rangeToMs(rangeId) / 1000),
    30 * 24 * 60 * 60,
  );
  return { timespan };
}

function timeseriesQueryForRange(
  rangeId: string,
): Record<string, string | number | boolean | undefined | null> {
  const custom = parseCustomRange(rangeId);
  if (custom) {
    return {
      t0: new Date(custom.from).toISOString(),
      t1: new Date(custom.to).toISOString(),
    };
  }
  const timespan = Math.min(
    Math.floor(rangeToMs(rangeId) / 1000),
    31 * 24 * 60 * 60,
  );
  return { timespan };
}

function isWithinRange(
  iso: string | null | undefined,
  rangeId: string,
): boolean {
  if (!iso) return false;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return false;
  const custom = parseCustomRange(rangeId);
  if (custom) return ts >= custom.from && ts <= custom.to;
  const cutoff = Date.now() - rangeToMs(rangeId);
  return ts >= cutoff;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString();
}

const THROUGHPUT_DAY_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const THROUGHPUT_TIME_FMT = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" });
const _dayKeyFmtCache = new Map<string, Intl.DateTimeFormat>();
const _dayLabelFmtCache = new Map<string, Intl.DateTimeFormat>();

function throughputTickLabel(iso: string, rangeId: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const custom = parseCustomRange(rangeId);
  const spanMs = custom ? custom.to - custom.from : rangeToMs(rangeId);
  if (spanMs > 48 * 60 * 60 * 1000) {
    return THROUGHPUT_DAY_FMT.format(dt);
  }
  return THROUGHPUT_TIME_FMT.format(dt);
}

function dayKeyForTimezone(iso: string, tz: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "unknown";
  let fmt = _dayKeyFmtCache.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });
    _dayKeyFmtCache.set(tz, fmt);
  }
  const parts = fmt.formatToParts(dt);
  const y = parts.find((p) => p.type === "year")?.value ?? "0000";
  const m = parts.find((p) => p.type === "month")?.value ?? "00";
  const d = parts.find((p) => p.type === "day")?.value ?? "00";
  return `${y}-${m}-${d}`;
}

function dayLabelForTimezone(iso: string, tz: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "Unknown day";
  let fmt = _dayLabelFmtCache.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(undefined, { timeZone: tz, weekday: "short", year: "numeric", month: "short", day: "numeric" });
    _dayLabelFmtCache.set(tz, fmt);
  }
  return fmt.format(dt);
}

function gmtOffsetLabel(offsetMinutes: number | null | undefined): string {
  if (offsetMinutes === null || offsetMinutes === undefined) return "GMT —";
  if (!Number.isFinite(offsetMinutes)) return "GMT —";
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60)
    .toString()
    .padStart(2, "0");
  const minutes = (abs % 60).toString().padStart(2, "0");
  return `GMT ${sign}${hours}:${minutes}`;
}

function longestConsecutiveDayStreak(dayKeys: string[]): number {
  if (dayKeys.length === 0) return 0;
  const dayMs = 24 * 60 * 60 * 1000;
  const values = dayKeys
    .flatMap((key) => {
      const ts = Date.parse(`${key}T00:00:00Z`);
      return Number.isFinite(ts) ? [ts] : [];
    })
    .sort((a, b) => a - b);
  if (values.length === 0) return 0;
  let best = 1;
  let current = 1;
  for (let i = 1; i < values.length; i += 1) {
    if (values[i] === values[i - 1]) continue;
    if (values[i] - values[i - 1] === dayMs) {
      current += 1;
      best = Math.max(best, current);
    } else {
      current = 1;
    }
  }
  return best;
}

function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function exportRowsCsv(
  rows: Array<Record<string, string | number | boolean | null | undefined>>,
  fileName: string,
) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escapeCell = (value: unknown) => {
    const text = String(value ?? "");
    const escaped = text.replace(/"/g, '""');
    return /[",\n]/.test(text) ? `"${escaped}"` : escaped;
  };
  const lines = [
    headers.map(escapeCell).join(","),
    ...rows.map((row) =>
      headers.map((header) => escapeCell(row[header])).join(","),
    ),
  ];
  triggerBlobDownload(
    new Blob([`${lines.join("\n")}\n`], { type: "text/csv;charset=utf-8;" }),
    `${fileName}.csv`,
  );
}

async function exportRowsXlsx(
  rows: Array<Record<string, string | number | boolean | null | undefined>>,
  fileName: string,
) {
  if (rows.length === 0) return;
  const XLSX = await import("xlsx");
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
  const bytes = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  triggerBlobDownload(
    new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${fileName}.xlsx`,
  );
}

function serialFromTopologyNode(node: Node): string | null {
  if (node.id === ISP_NODE_ID || node.id.startsWith(AP_GROUP_PREFIX)) {
    return null;
  }
  const device = (node.data as { device?: TopologyDevice } | undefined)
    ?.device;
  if (!device) return null;
  if (
    device.serial === ISP_NODE_ID ||
    String(device.serial).startsWith(AP_GROUP_PREFIX)
  ) {
    return null;
  }
  return device?.serial ?? null;
}

/* ============================================================
 * PAGE
 * ============================================================ */

function useSiteOverviewPage(chartExportRef: React.RefObject<HTMLDivElement>) {
  const themeMode = useTheme((s) => s.resolved);

  const { slug = "wasci" } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // useShallow prevents a full re-render when unrelated sites update in the store
  // (e.g. the sites list refetching should not re-render the entire dashboard).
  const {
    bySlug,
    sectionStatusBySlug,
    sites,
    error,
    loadSites,
    loadDashboard,
    loadDashboardSection,
  } = useSites(
    useShallow((s) => ({
      bySlug: s.bySlug,
      sectionStatusBySlug: s.sectionStatusBySlug,
      sites: s.sites,
      error: s.error,
      loadSites: s.loadSites,
      loadDashboard: s.loadDashboard,
      loadDashboardSection: s.loadDashboardSection,
    })),
  );
  const aiLoad = useAiExplain((s) => s.load);

  const [range, setRange] = useState("24h");
  const [refreshingSection, setRefreshingSection] =
    useState<DashboardSection | null>(null);
  const [topologyQuery, setTopologyQuery] = useState("");
  const [selectedDeviceClasses, setSelectedDeviceClasses] = useState<
    Set<TopologyDeviceClass>
  >(new Set());
  const [throughputGraph, setThroughputGraph] = useState<ThroughputGraph>(
    "applianceUplinkBandwidth",
  );
  const [visibleThroughputSeries, setVisibleThroughputSeries] = useState<
    Record<ThroughputSeriesKey, boolean>
  >({
    Received: true,
    Sent: true,
  });
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showThroughputPercent, setShowThroughputPercent] = useState(false);
  const [showRxLimitLine, setShowRxLimitLine] = useState(false);
  const [showTxLimitLine, setShowTxLimitLine] = useState(false);
  const [exportingDevicesXlsx, setExportingDevicesXlsx] = useState(false);
  const [networkDevicesView, setNetworkDevicesView] =
    useState<NetworkDevicesView>("diagram");
  const [expandedApGroups, setExpandedApGroups] = useState<Set<string>>(
    () => new Set(),
  );
  const topologyBySlug = useTopologyLinkLayer((s) => s.bySlug);
  const loadTopologyLinkLayer = useTopologyLinkLayer((s) => s.load);
  const topologyDetailsBySerial = useTopologyDeviceDetails((s) => s.bySerial);
  const topologyDetailsLoading = useTopologyDeviceDetails((s) => s.loading);
  const topologyDetailsErrors = useTopologyDeviceDetails((s) => s.errors);
  const loadTopologyDetails = useTopologyDeviceDetails((s) => s.load);
  const [alertsTab, setAlertsTab] = useState<AlertsTab>("networkEvents");
  const [summarizing, setSummarizing] = useState(false);
  const [summaryState, setSummaryState] = useState<{
    key: string;
    data: AiExplanation | null;
    error: string | null;
  }>({ key: "", data: null, error: null });
  const summaryKey = `${slug}:${String(range)}`;
  const summaryData = summaryState.key === summaryKey ? summaryState.data : null;
  const summaryError = summaryState.key === summaryKey ? summaryState.error : null;
  const [tagFilter, setTagFilter] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<
    Set<"online" | "alerting" | "offline" | "dormant">
  >(new Set());
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const copyResetRef = useRef<number | null>(null);
  // Ref mirrors for summarize auto-trigger guard — avoids listing state in
  // the effect deps (which would cause an extra render per state transition).
  const summarizingRef = useRef(false);
  const autoSummarizedContextRef = useRef<unknown>(null);
  const activeNetworkSection: DashboardSection =
    networkDevicesView === "topology" ? "topology" : "devices";

  // Status filter callbacks
  const handleToggleStatus = (
    status: "online" | "alerting" | "offline" | "dormant",
  ) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const handleResetStatuses = () => {
    setSelectedStatuses(new Set());
  };

  function copyPropertyField(
    value: string | null | undefined,
    fieldKey: string,
  ) {
    const text = (value ?? "").trim();
    if (!text) return;
    void navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldKey);
      if (copyResetRef.current !== null) {
        window.clearTimeout(copyResetRef.current);
      }
      copyResetRef.current = window.setTimeout(() => {
        setCopiedField((current) => (current === fieldKey ? null : current));
      }, 1500);
    });
  }

  async function refreshSection(section: DashboardSection) {
    logger.info({ slug, section, range }, "dashboard_refresh_section");
    setRefreshingSection(section);
    try {
      if (section === "overview") {
        await loadDashboard(slug, {
          force: true,
          clientsQuery: clientsQueryForRange(range),
          timeseriesQuery: timeseriesQueryForRange(range),
        });
        return;
      }
      if (section === "throughput") {
        await Promise.all([
          loadDashboardSection(slug, "throughput", {
            force: true,
            timeseriesQuery: timeseriesQueryForRange(range),
          }),
          loadDashboardSection(slug, "clientBandwidthUsage", {
            force: true,
            timeseriesQuery: timeseriesQueryForRange(range),
          }),
          loadDashboardSection(slug, "wanUplinks", { force: true }),
        ]);
        return;
      }
      if (section === "topology" || section === "devices") {
        await loadDashboardSection(slug, "devices", { force: true });
        return;
      }
      if (section === "clients") {
        await loadDashboardSection(slug, "clients", {
          force: true,
          clientsQuery: clientsQueryForRange(range),
        });
        return;
      }
      if (section === "firmware") {
        await loadDashboardSection(slug, "firmwareUpgrades", { force: true });
        return;
      }
      if (section === "licenses") {
        await loadDashboardSection(slug, "licenses", { force: true });
        return;
      }
      if (section === "alerts") {
        await loadDashboardSection(slug, "alertsHistory", { force: true });
        return;
      }
      if (section === "assuranceAlerts") {
        await loadDashboardSection(slug, "assuranceAlerts", {
          force: true,
          timeseriesQuery: timeseriesQueryForRange(range),
        });
        return;
      }
      if (section === "wirelessFailedConnections") {
        await loadDashboardSection(slug, "wirelessFailedConnections", {
          force: true,
          timeseriesQuery: timeseriesQueryForRange(range),
        });
      }
    } finally {
      setRefreshingSection((current) => (current === section ? null : current));
    }
  }

  const sectionState = sectionStatusBySlug[slug] ?? {
    summary: "idle",
    rollups: "idle",
    wanUplinks: "idle",
    throughput: "idle",
    clientBandwidthUsage: "idle",
    devices: "idle",
    clients: "idle",
    firmwareUpgrades: "idle",
    alertsHistory: "idle",
    licenses: "idle",
    assuranceAlerts: "idle",
    wirelessFailedConnections: "idle",
  };

  function sectionLoading(section: DashboardSection) {
    if (refreshingSection === section) return true;
    if (section === "overview") {
      return (
        sectionState.summary === "loading" || sectionState.rollups === "loading"
      );
    }
    if (section === "throughput") {
      return (
        sectionState.throughput === "loading" ||
        sectionState.clientBandwidthUsage === "loading" ||
        sectionState.wanUplinks === "loading"
      );
    }
    if (section === "topology" || section === "devices") {
      return sectionState.devices === "loading";
    }
    if (section === "clients") return sectionState.clients === "loading";
    if (section === "firmware")
      return sectionState.firmwareUpgrades === "loading";
    if (section === "licenses") return sectionState.licenses === "loading";
    if (section === "alerts") return sectionState.alertsHistory === "loading";
    if (section === "assuranceAlerts") {
      return sectionState.assuranceAlerts === "loading";
    }
    return sectionState.wirelessFailedConnections === "loading";
  }

  // Initial load — bring the site list (for the picker) AND this site's dashboard.
  useEffect(() => {
    void loadSites();
  }, [loadSites]);
  useEffect(() => {
    logger.info({ slug, range }, "dashboard_load_start");
    void loadDashboard(slug, {
      force: true,
      clientsQuery: clientsQueryForRange(range),
      timeseriesQuery: timeseriesQueryForRange(range),
    });
  }, [slug, range, loadDashboard]);

  useEffect(() => {
    void loadTopologyLinkLayer(slug);
  }, [loadTopologyLinkLayer, slug]);

  const dashboard = bySlug[slug];
  const sectionFetchedAt = dashboard?._sectionFetchedAt ?? {};
  const applianceThroughput = dashboard?.throughput ?? [];
  const clientBandwidthUsage = dashboard?.clientBandwidthUsage ?? [];
  const throughput =
    throughputGraph === "applianceUplinkBandwidth"
      ? applianceThroughput
      : clientBandwidthUsage;
  const devices = useMemo(() => dashboard?.devices ?? [], [dashboard]);

  const topologyScopedDevices = useMemo<TopologyDevice[]>(() => {
    const withWan: TopologyDevice[] = [
      {
        serial: "wan",
        name: "Internet WAN",
        model: "Internet",
        macAddress: null,
        address: null,
        tags: [],
        notes: null,
        status: "online",
        lanIp: null,
        publicIp: null,
        lastSeen: null,
        deviceClass: "wan",
      },
      ...devices,
    ];
    const q = topologyQuery.trim().toLowerCase();
    const tagQ = tagFilter.trim().toLowerCase();
    return withWan.filter((d) => {
      if (
        selectedDeviceClasses.size > 0 &&
        !selectedDeviceClasses.has(d.deviceClass)
      ) {
        return false;
      }
      if (
        tagQ &&
        !(d.tags ?? []).some((tag) => tag.toLowerCase().includes(tagQ))
      ) {
        return false;
      }

      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        d.serial.toLowerCase().includes(q) ||
        (d.macAddress ?? "").toLowerCase().includes(q) ||
        (d.address ?? "").toLowerCase().includes(q) ||
        (d.tags ?? []).some((tag) => tag.toLowerCase().includes(q))
      );
    });
  }, [devices, topologyQuery, selectedDeviceClasses, tagFilter]);

  // Apply status filtering on top of topology scoped devices
  const filteredTopologyDevices = useMemo<TopologyDevice[]>(() => {
    if (selectedStatuses.size === 0) return topologyScopedDevices;
    return topologyScopedDevices.filter((d) => selectedStatuses.has(d.status));
  }, [topologyScopedDevices, selectedStatuses]);

  // Calculate status counts for all non-WAN devices (without status filter)
  // so the legend shows available statuses to filter by
  const statusCounts = useMemo(() => {
    const counts: Partial<
      Record<"online" | "alerting" | "offline" | "dormant", number>
    > = {
      online: 0,
      alerting: 0,
      offline: 0,
      dormant: 0,
    };
    topologyScopedDevices.forEach((d) => {
      if (d.deviceClass !== "wan" && d.status in counts) {
        counts[d.status as "online" | "alerting" | "offline" | "dormant"]!++;
      }
    });
    return counts;
  }, [topologyScopedDevices]);

  const filteredDevices = useMemo<NonWanDevice[]>(
    () =>
      filteredTopologyDevices.filter(
        (d): d is NonWanDevice => d.deviceClass !== "wan",
      ),
    [filteredTopologyDevices],
  );

  const topologyExplorerFallbackNodes = useMemo<Node[]>(() => {
    const classOrder: TopologyDeviceClass[] = [
      "wan",
      "appliance",
      "switch",
      "wireless",
    ];
    const byClass = new Map<TopologyDeviceClass, TopologyDevice[]>();
    classOrder.forEach((k) => byClass.set(k, []));
    filteredTopologyDevices.forEach((d) => {
      const bucket = byClass.get(d.deviceClass) ?? [];
      bucket.push(d);
      byClass.set(d.deviceClass, bucket);
    });

    const nodes: Node[] = [];
    const COLUMN_W = 320;
    const ROW_H = 70;
    classOrder.forEach((deviceClass, classIndex) => {
      const bucket = byClass.get(deviceClass) ?? [];
      bucket.forEach((device, rowIndex) => {
        nodes.push({
          id: device.serial,
          position: { x: classIndex * COLUMN_W, y: rowIndex * ROW_H },
          data: {
            label: `${device.name} · ${device.status}`,
            device,
          },
        });
      });
    });
    return nodes;
  }, [filteredTopologyDevices]);

  const topologyExplorerFallbackEdges = useMemo<Edge[]>(() => {
    const byClass = {
      wan: filteredTopologyDevices.filter((d) => d.deviceClass === "wan"),
      appliance: filteredTopologyDevices.filter(
        (d) => d.deviceClass === "appliance",
      ),
      switch: filteredTopologyDevices.filter((d) => d.deviceClass === "switch"),
      wireless: filteredTopologyDevices.filter(
        (d) => d.deviceClass === "wireless",
      ),
    };
    const edges: Edge[] = [];

    const wanSerial = byClass.wan[0]?.serial;
    byClass.appliance.forEach((device, idx) => {
      if (!wanSerial) return;
      edges.push({
        id: `wan-appliance-${idx}`,
        source: wanSerial,
        target: device.serial,
      });
    });

    const applianceAnchor = byClass.appliance[0]?.serial ?? wanSerial;
    byClass.switch.forEach((device, idx) => {
      if (!applianceAnchor) return;
      edges.push({
        id: `appliance-switch-${idx}`,
        source: applianceAnchor,
        target: device.serial,
      });
    });

    const switchAnchor = byClass.switch[0]?.serial ?? applianceAnchor;
    byClass.wireless.forEach((device, idx) => {
      if (!switchAnchor) return;
      edges.push({
        id: `switch-wireless-${idx}`,
        source: switchAnchor,
        target: device.serial,
      });
    });

    return edges;
  }, [filteredTopologyDevices]);

  const topologyDevicesBySerial = useMemo(
    () =>
      new Map(
        filteredTopologyDevices.flatMap((d) =>
          d.deviceClass !== "wan" ? [[d.serial, d] as const] : [],
        ),
      ),
    [filteredTopologyDevices],
  );

  const topologyLayout = useMemo(() => {
    const linkLayer = topologyBySlug[slug.toLowerCase()];
    if (linkLayer?.nodes.length) {
      const llLayout = buildDashboardLinkLayerLayout(
        linkLayer,
        topologyDevicesBySerial,
        expandedApGroups,
      );
      if (llLayout.nodes.length > 0) {
        return { ...llLayout, mode: "link-layer" as const };
      }
    }
    const fallbackFocus =
      topologyExplorerFallbackNodes.find((n) => {
        const device = (n.data as { device?: TopologyDevice } | undefined)
          ?.device;
        return device?.deviceClass === "appliance";
      })?.id ??
      topologyExplorerFallbackNodes[0]?.id ??
      null;
    return {
      nodes: topologyExplorerFallbackNodes,
      edges: topologyExplorerFallbackEdges,
      mode: "class" as const,
      focusNodeId: fallbackFocus,
    };
  }, [
    expandedApGroups,
    slug,
    topologyBySlug,
    topologyDevicesBySerial,
    topologyExplorerFallbackEdges,
    topologyExplorerFallbackNodes,
  ]);

  useEffect(() => {
    setExpandedApGroups(new Set());
  }, [slug]);

  const filteredRollups = useMemo<DeviceClassRollup[]>(() => {
    type RollupClass = Exclude<SiteDevice["deviceClass"], "wan">;
    const byClass: Record<RollupClass, NonWanDevice[]> = {
      appliance: [],
      switch: [],
      wireless: [],
    };
    for (const d of filteredDevices) byClass[d.deviceClass].push(d);

    const base = [
      { label: "Appliances", key: "appliance" as const },
      { label: "Switches", key: "switch" as const },
      { label: "Wireless", key: "wireless" as const },
    ];

    return base.map(({ label, key }) => {
      const rows = byClass[key];
      const byStatus: Record<SiteDevice["status"], number> = {
        online: 0,
        alerting: 0,
        offline: 0,
        dormant: 0,
      };
      for (const r of rows) byStatus[r.status] += 1;
      return {
        label,
        total: rows.length,
        byStatus,
      };
    });
  }, [filteredDevices]);

  function toggleThroughputSeries(series: ThroughputSeriesKey) {
    setVisibleThroughputSeries((prev) => {
      const next = { ...prev, [series]: !prev[series] };
      if (!next.Received && !next.Sent) return prev;
      return next;
    });
  }

  const networkDevicesExportRows = useMemo(
    () =>
      filteredDevices.map((device) => ({
        Name: device.name,
        Serial: device.serial,
        Model: device.model,
        Status: device.status,
        "LAN IP": device.lanIp ?? "",
        "Public IP": device.publicIp ?? "",
        Address: device.address ?? "",
        Tags: (device.tags ?? []).join(", "),
        "Last Seen": device.lastSeen ?? "",
      })),
    [filteredDevices],
  );

  function downloadNetworkDevicesCsv() {
    exportRowsCsv(networkDevicesExportRows, "network-devices");
  }

  async function downloadNetworkDevicesXlsx() {
    setExportingDevicesXlsx(true);
    try {
      await exportRowsXlsx(networkDevicesExportRows, "network-devices");
    } finally {
      setExportingDevicesXlsx(false);
    }
  }

  // Memoized: reshape throughput for Recharts and compute per-series stats.
  // Split into two memos so activeSeries (cheap, toggle-driven) doesn't re-run
  // the expensive array passes over throughput whenever a series is toggled.
  const activeSeries = useMemo(
    () =>
      (
        Object.entries(visibleThroughputSeries) as Array<
          [ThroughputSeriesKey, boolean]
        >
      ).flatMap(([series, shown]) => (shown ? [series] : [])),
    [visibleThroughputSeries],
  );

  const { chartData, peak, rxAvg, txAvg, rxMax, txMax, rxMin, txMin } =
    useMemo(() => {
      const data = throughput.map((p: ThroughputPoint) => ({
        t: throughputTickLabel(p.t, range),
        iso: p.t,
        Received: p.rxBps,
        Sent: p.txBps,
        Total: p.rxBps + p.txBps,
      }));
      const totals = throughput.map((p) => p.rxBps + p.txBps);
      const pk = Math.max(...totals, 1);
      const rxVals = throughput.map((p) => p.rxBps);
      const txVals = throughput.map((p) => p.txBps);
      const n = Math.max(throughput.length, 1);
      return {
        chartData: data,
        peak: pk,
        rxAvg: rxVals.reduce((acc, v) => acc + v, 0) / n,
        txAvg: txVals.reduce((acc, v) => acc + v, 0) / n,
        rxMax: rxVals.length > 0 ? Math.max(...rxVals) : 0,
        txMax: txVals.length > 0 ? Math.max(...txVals) : 0,
        rxMin: rxVals.length > 0 ? Math.min(...rxVals) : 0,
        txMin: txVals.length > 0 ? Math.min(...txVals) : 0,
      };
    }, [throughput, range]);

  const yAxisMax = useMemo(() => {
    if (chartData.length === 0 || activeSeries.length === 0) return 1;
    const maxVisible = chartData.reduce((acc, point) => {
      const pointMax = Math.max(
        ...activeSeries.map((series) => point[series] as number),
      );
      return Math.max(acc, pointMax);
    }, 0);
    return Math.max(1, Math.ceil(maxVisible * 1.1));
  }, [activeSeries, chartData]);

  const throughputGraphLabel =
    throughputGraph === "applianceUplinkBandwidth"
      ? "Appliance Uplink Bandwidth"
      : "Client Bandwidth Usage";
  const throughputGraphSlug =
    throughputGraph === "applianceUplinkBandwidth"
      ? "appliance-uplink-bandwidth"
      : "client-bandwidth-usage";

  function downloadThroughputCsv(siteCode: string) {
    if (chartData.length === 0 || activeSeries.length === 0) return;
    const headers = [
      "time_utc",
      ...activeSeries.map((s) => `${s.toLowerCase()}_bps`),
    ];
    const body = chartData
      .map((point) => {
        const row = [
          point.iso,
          ...activeSeries.map((series) => String(point[series])),
        ];
        return row.join(",");
      })
      .join("\n");
    const blob = new Blob([`${headers.join(",")}\n${body}\n`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${siteCode.toLowerCase()}-${throughputGraphSlug}-${range}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  async function downloadThroughputPdf(siteCode: string) {
    if (!summary || !chartExportRef.current || chartData.length === 0) return;
    const summaryForExport = summary;
    setExportingPdf(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const canvas = await html2canvas(chartExportRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const image = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 28;
      const titleY = 28;
      const availableWidth = pageWidth - margin * 2;
      const subtitleLines = [
        `Graph: ${throughputGraphLabel}`,
        `Network: ${summaryForExport.friendlyName}`,
        `Organization: ${summaryForExport.org}`,
        `Network ID: ${summaryForExport.networkId}`,
        `Date range: ${rangeLabel(range)}`,
      ];
      const availableHeight = 270;
      const scale = Math.min(
        availableWidth / canvas.width,
        availableHeight / canvas.height,
      );
      const targetWidth = canvas.width * scale;
      const targetHeight = canvas.height * scale;
      const x = (pageWidth - targetWidth) / 2;
      const y = 118;

      pdf.setFontSize(16);
      pdf.text(`${siteCode} Throughput Report`, margin, titleY);
      pdf.setFontSize(10);
      subtitleLines.forEach((line, index) => {
        pdf.text(line, margin, titleY + 18 + index * 14);
      });
      pdf.addImage(image, "PNG", x, y, targetWidth, targetHeight);

      const columns = [
        { title: "Time (UTC)", x: margin, width: 210 },
        ...activeSeries.map((series, index) => ({
          title: `${series} (bps)`,
          x: margin + 230 + index * 165,
          width: 150,
        })),
      ];
      let cursorY = y + targetHeight + 28;
      const drawHeader = () => {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        columns.forEach((column) => {
          pdf.text(column.title, column.x, cursorY);
        });
        pdf.setDrawColor(200, 204, 214);
        pdf.line(margin, cursorY + 6, pageWidth - margin, cursorY + 6);
        pdf.setFont("helvetica", "normal");
        cursorY += 20;
      };

      drawHeader();
      chartData.forEach((point) => {
        if (cursorY > pageHeight - margin) {
          pdf.addPage();
          cursorY = margin;
          drawHeader();
        }
        const cells = [
          fmtDateTime(point.iso),
          ...activeSeries.map((series) => String(point[series])),
        ];
        cells.forEach((cell, index) => {
          const column = columns[index];
          pdf.text(pdf.splitTextToSize(cell, column.width), column.x, cursorY);
        });
        cursorY += 16;
      });

      pdf.save(`${siteCode.toLowerCase()}-${throughputGraphSlug}-${range}.pdf`);
    } finally {
      setExportingPdf(false);
    }
  }

  const firmwareUpgrades = useMemo(() => dashboard?.firmwareUpgrades ?? [], [dashboard]);
  const licenses = useMemo(() => dashboard?.licenses ?? [], [dashboard]);
  const alertsHistory = dashboard?.alertsHistory ?? [];
  const assuranceAlerts = useMemo(() => dashboard?.assuranceAlerts ?? [], [dashboard]);
  const wirelessFailedConnections = useMemo(() => dashboard?.wirelessFailedConnections ?? [], [dashboard]);
  const summary = dashboard?.summary;
  const wanUplinks = dashboard?.wanUplinks ?? [];
  const clients = dashboard?.clients ?? [];
  const applianceCount =
    filteredRollups.find((r) => r.label === "Appliances")?.total ?? 0;
  const applianceStatusCards =
    applianceCount <= 1 ? wanUplinks.slice(0, 1) : wanUplinks;
  const showRollupsSkeleton =
    sectionState.devices === "loading" && filteredRollups.length === 0;
  const showThroughputSkeleton =
    ((throughputGraph === "applianceUplinkBandwidth" &&
      sectionState.throughput === "loading") ||
      (throughputGraph === "clientBandwidthUsage" &&
        sectionState.clientBandwidthUsage === "loading")) &&
    throughput.length === 0;
  const showWanSkeleton =
    sectionState.wanUplinks === "loading" && wanUplinks.length === 0;
  const showDevicesSkeleton =
    sectionState.devices === "loading" && filteredDevices.length === 0;
  const showClientsSkeleton =
    sectionState.clients === "loading" && clients.length === 0;
  const showFirmwareSkeleton =
    sectionState.firmwareUpgrades === "loading" &&
    firmwareUpgrades.length === 0;
  const showAlertsSkeleton =
    sectionState.alertsHistory === "loading" && alertsHistory.length === 0;
  const showLicensesSkeleton =
    sectionState.licenses === "loading" && licenses.length === 0;
  const showAssuranceSkeleton =
    sectionState.assuranceAlerts === "loading" && assuranceAlerts.length === 0;
  const showConnectionsSkeleton =
    sectionState.wirelessFailedConnections === "loading" &&
    wirelessFailedConnections.length === 0;
  const anySectionLoading = Object.values(sectionState).some(
    (state) => state === "loading",
  );
  const summarizeReady = !!summary && !anySectionLoading;
  const downLimitBps = (summary?.rxLimitKbps ?? 0) * 1_000;
  const upLimitBps = (summary?.txLimitKbps ?? 0) * 1_000;
  const hasThroughputLimits = downLimitBps > 0 || upLimitBps > 0;
  const rxExceeded =
    downLimitBps > 0 && throughput.some((point) => point.rxBps > downLimitBps);
  const txExceeded =
    upLimitBps > 0 && throughput.some((point) => point.txBps > upLimitBps);
  const showRxLimit =
    hasThroughputLimits && showRxLimitLine && downLimitBps > 0;
  const showTxLimit = hasThroughputLimits && showTxLimitLine && upLimitBps > 0;
  const showBandwidthLimits = showRxLimit || showTxLimit;
  const throughputTimezone = summary?.timezoneName || "UTC";
  const overuseDayKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const point of throughput) {
      const overRx = downLimitBps > 0 && point.rxBps > downLimitBps;
      const overTx = upLimitBps > 0 && point.txBps > upLimitBps;
      if (!overRx && !overTx) continue;
      keys.add(dayKeyForTimezone(point.t, throughputTimezone));
    }
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }, [downLimitBps, throughput, throughputTimezone, upLimitBps]);
  const overuseConsecutiveDays = longestConsecutiveDayStreak(overuseDayKeys);
  const hasOveruse = overuseDayKeys.length > 0;
  const percentageLimitBase = Math.max(downLimitBps, upLimitBps, 0);
  const throughputValueFormatter =
    showThroughputPercent && percentageLimitBase > 0
      ? (value: number) => `${Math.round((value / percentageLimitBase) * 100)}%`
      : fmtBps;

  const dashboardSummaryContext = useMemo(() => {
    if (!summary) return null;

    const healthBySeverity: Record<string, number> = {};
    for (const row of assuranceAlerts) {
      const key = (row.severity || "info").toLowerCase();
      healthBySeverity[key] = (healthBySeverity[key] ?? 0) + 1;
    }

    const connectionByType: Record<string, number> = {};
    for (const row of wirelessFailedConnections) {
      const key = row.type || "unknown";
      connectionByType[key] = (connectionByType[key] ?? 0) + 1;
    }

    const offlineDevices = filteredDevices.filter(
      (device) => device.status === "offline",
    ).length;
    const alertingDevices = filteredDevices.filter(
      (device) => device.status === "alerting",
    ).length;
    const dormantDevices = filteredDevices.filter(
      (device) => device.status === "dormant",
    ).length;
    const devicesWithoutAddress = filteredDevices.filter(
      (device) => !(device.address ?? "").trim(),
    ).length;
    const criticalHealthAlerts = healthBySeverity.critical ?? 0;
    const warningHealthAlerts =
      (healthBySeverity.warning ?? 0) + (healthBySeverity.major ?? 0);
    const wanActive = summary.uplinks.active;
    const wanTotal = summary.uplinks.total;
    const wanDegraded = wanTotal > 0 && wanActive < wanTotal;
    const topConnectionTypes = Object.entries(connectionByType)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([type, count]) => ({ type, count }));
    const topProblemDevices = filteredDevices
      .filter(
        (device) => device.status === "offline" || device.status === "alerting",
      )
      .slice(0, 10)
      .map((device) => ({
        name: device.name,
        serial: device.serial,
        status: device.status,
        model: device.model,
        lastSeen: device.lastSeen,
      }));
    const topHealthCategories = Object.entries(healthBySeverity)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([severity, count]) => ({ severity, count }));

    const prioritySignals: string[] = [];
    if (criticalHealthAlerts > 0) {
      prioritySignals.push(
        `${criticalHealthAlerts} critical health alert${criticalHealthAlerts === 1 ? "" : "s"}`,
      );
    }
    if (offlineDevices > 0) {
      prioritySignals.push(
        `${offlineDevices} offline device${offlineDevices === 1 ? "" : "s"}`,
      );
    }
    if (alertingDevices > 0) {
      prioritySignals.push(
        `${alertingDevices} alerting device${alertingDevices === 1 ? "" : "s"}`,
      );
    }
    if (wanDegraded) {
      prioritySignals.push(
        `WAN degraded (${wanActive}/${wanTotal} active uplinks)`,
      );
    }
    if (wirelessFailedConnections.length >= 10) {
      prioritySignals.push(
        `${wirelessFailedConnections.length} wireless connection failures in range`,
      );
    }
    if (firmwareUpgrades.some((upgrade) => upgrade.phase === "upcoming")) {
      prioritySignals.push(
        `${firmwareUpgrades.filter((upgrade) => upgrade.phase === "upcoming").length} pending firmware upgrade${
          firmwareUpgrades.filter((upgrade) => upgrade.phase === "upcoming")
            .length === 1
            ? ""
            : "s"
        }`,
      );
    }

    const overallRisk =
      criticalHealthAlerts > 0 || offlineDevices > 0 || wanDegraded
        ? "high"
        : warningHealthAlerts > 0 ||
            alertingDevices > 0 ||
            wirelessFailedConnections.length > 0
          ? "medium"
          : "low";

    return {
      site: {
        slug,
        code: summary.code,
        name: summary.friendlyName,
        org: summary.org,
        networkId: summary.networkId,
        range: rangeLabel(range),
      },
      kpis: {
        activeClients: summary.activeClients,
        heavyClientsLastHour: summary.heavyClientsLastHour,
        peakThroughputBps: peak,
      },
      wan: {
        uplinks: summary.uplinks,
        applianceStatusCards: applianceStatusCards.map((u) => ({
          label: u.label,
          state: u.state,
          publicIp: u.publicIp,
          lanIp: u.lanIp,
        })),
      },
      throughput: {
        graph: throughputGraph,
        samples: throughput.length,
        latest:
          throughput.length > 0 ? throughput[throughput.length - 1] : null,
      },
      assessmentSignals: {
        overallRisk,
        wanDegraded,
        offlineDevices,
        alertingDevices,
        dormantDevices,
        criticalHealthAlerts,
        warningHealthAlerts,
        devicesWithoutAddress,
        topHealthCategories,
        topConnectionTypes,
        topProblemDevices,
        prioritySignals,
      },
      deviceRollups: filteredRollups,
      deviceCounts: {
        totalShown: filteredDevices.length,
        online: filteredDevices.filter((d) => d.status === "online").length,
        alerting: alertingDevices,
        offline: offlineDevices,
        dormant: dormantDevices,
      },
      alerts: {
        networkEvents: alertsHistory.length,
        healthTotal: assuranceAlerts.length,
        healthBySeverity,
        connectionFailuresTotal: wirelessFailedConnections.length,
        connectionByType,
      },
      licenses: {
        total: licenses.length,
        expired: licenses.filter((row) =>
          (row.state ?? "").toLowerCase().includes("expired"),
        ).length,
        expiring: licenses.filter((row) =>
          (row.state ?? "").toLowerCase().includes("expire"),
        ).length,
      },
      firmware: {
        total: firmwareUpgrades.length,
        upcoming: firmwareUpgrades.filter((f) => f.phase === "upcoming").length,
        completed: firmwareUpgrades.filter((f) => f.phase === "completed")
          .length,
      },
      clients: {
        rows: clients.length,
      },
    };
  }, [
    alertsHistory.length,
    applianceStatusCards,
    assuranceAlerts,
    clients.length,
    filteredDevices,
    filteredRollups,
    firmwareUpgrades,
    licenses,
    peak,
    range,
    slug,
    summary,
    throughput,
    throughputGraph,
    wirelessFailedConnections,
  ]);

  const summarizeDashboard = useCallback(async (force = false) => {
    if (!summarizeReady || !dashboardSummaryContext) return;
    const key = `${slug}:${String(range)}`;
    summarizingRef.current = true;
    setSummarizing(true);
    setSummaryState((prev) => ({ ...prev, key, error: null }));
    try {
      const result = await aiLoad(
        "site-dashboard-summary",
        dashboardSummaryContext,
        force ? { force: true } : undefined,
      );
      if (result) setSummaryState({ key, data: result, error: null });
    } catch (e) {
      setSummaryState({
        key,
        data: null,
        error: e instanceof Error ? e.message : "Failed to summarize dashboard",
      });
    } finally {
      summarizingRef.current = false;
      setSummarizing(false);
    }
  }, [summarizeReady, dashboardSummaryContext, aiLoad, slug, range]);

  const gatheringSummaryData =
    (!summarizeReady || !dashboardSummaryContext) &&
    !summarizing &&
    !summaryData &&
    !summaryError;
  const computingSummary = summarizing;
  const showResummarize = Boolean(
    summaryData &&
    summarizeReady &&
    dashboardSummaryContext &&
    !computingSummary,
  );

  // Auto-trigger once per context snapshot when all data is ready.
  // Refs guard against double-trigger; no state in deps to avoid extra renders.
  useEffect(() => {
    if (!summarizeReady || !dashboardSummaryContext) return;
    if (summarizingRef.current || autoSummarizedContextRef.current === dashboardSummaryContext) return;
    autoSummarizedContextRef.current = dashboardSummaryContext;
    void summarizeDashboard(false);
  }, [summarizeReady, dashboardSummaryContext, summarizeDashboard]);

  useEffect(() => {
    setShowRxLimitLine((prev) => prev || rxExceeded);
    setShowTxLimitLine((prev) => prev || txExceeded);
  }, [rxExceeded, txExceeded]);

  return {
    themeMode,
    slug,
    navigate,
    sites,
    error,
    range,
    setRange,
    summary,
    sectionLoading,
    refreshSection,
    copiedField,
    copyPropertyField,
    showResummarize,
    summarizeReady,
    summarizing,
    gatheringSummaryData,
    computingSummary,
    summaryError,
    summaryData,
    summarizeDashboard,
    showRollupsSkeleton,
    filteredRollups,
    peak,
    showThroughputSkeleton,
    throughputGraph,
    setThroughputGraph,
    throughputGraphLabel,
    sectionFetchedAt,
    chartData,
    activeSeries,
    downloadThroughputCsv,
    downloadThroughputPdf,
    exportingPdf,
    hasThroughputLimits,
    hasOveruse,
    overuseConsecutiveDays,
    setShowThroughputPercent,
    showThroughputPercent,
    visibleThroughputSeries,
    toggleThroughputSeries,
    showBandwidthLimits,
    setShowRxLimitLine,
    setShowTxLimitLine,
    yAxisMax,
    percentageLimitBase,
    throughputValueFormatter,
    rxAvg,
    rxMax,
    rxMin,
    txAvg,
    txMax,
    txMin,
    downLimitBps,
    upLimitBps,
    showRxLimit,
    showTxLimit,
    showWanSkeleton,
    applianceStatusCards,
    networkDevicesView,
    setNetworkDevicesView,
    activeNetworkSection,
    showDevicesSkeleton,
    filteredDevices,
    topologyQuery,
    setTopologyQuery,
    selectedDeviceClasses,
    setSelectedDeviceClasses,
    tagFilter,
    setTagFilter,
    statusCounts,
    selectedStatuses,
    handleToggleStatus,
    handleResetStatuses,
    topologyLayout,
    setExpandedApGroups,
    topologyDetailsBySerial,
    topologyDetailsLoading,
    topologyDetailsErrors,
    loadTopologyDetails,
    serialFromTopologyNode,
    networkDevicesExportRows,
    downloadNetworkDevicesCsv,
    downloadNetworkDevicesXlsx,
    exportingDevicesXlsx,
    showFirmwareSkeleton,
    firmwareUpgrades,
    licenses,
    showLicensesSkeleton,
    alertsHistory,
    assuranceAlerts,
    wirelessFailedConnections,
    alertsTab,
    setAlertsTab,
    showAlertsSkeleton,
    showAssuranceSkeleton,
    showConnectionsSkeleton,
    clients,
    showClientsSkeleton,
  };
}

type SiteOverviewPageState = ReturnType<typeof useSiteOverviewPage>;

function SiteDashboardHeaderSection({ p }: { p: SiteOverviewPageState }) {
  const summary = p.summary!;
  return (
    <>
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_200px_auto] lg:items-end">
            <label htmlFor="dashboard-site-picker" className="flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
                Site
              </span>
              <SitePickerInline
                inputId="dashboard-site-picker"
                value={p.slug}
                sites={p.sites.length > 0 ? p.sites : [summary]}
                onChange={(s) => p.navigate(`/sites/${s}`)}
              />
            </label>
            <label htmlFor="dashboard-range-picker" className="flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
                Range
              </span>
              <RangePicker
                id="dashboard-range-picker"
                value={p.range}
                onChange={p.setRange}
                options={RANGE_OPTIONS}
                rangeLabel={rangeLabel}
                parseCustomRange={parseCustomRange}
                customRangePrefix={CUSTOM_RANGE_PREFIX}
              />
            </label>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => void p.refreshSection("overview")}
                disabled={p.sectionLoading("overview")}
              >
                <RefreshCw
                  size={13}
                  className={cn(p.sectionLoading("overview") && "animate-spin")}
                />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[340px_1fr]">
        <Card>
          <CardContent className="flex h-full flex-col gap-2">
            <CardTitle>Property Information</CardTitle>
            <CardSub>
              {summary.synapseAvailable === false
                ? "Synapse details are currently unavailable; showing vendor data without Property association."
                : "Synapse property details linked to this site."}
            </CardSub>
            <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[11px]">
              <div className="text-fg-mute">Hotel</div>
              <div className="text-fg">{summary.friendlyName}</div>
              <div className="text-fg-mute">
                {(summary.brandName ?? "").toLowerCase().includes("marriott")
                  ? "Marsha code"
                  : "Inncode"}
              </div>
              <div className="font-mono text-fg">
                {summary.innCode ?? summary.code ?? "—"}
              </div>
              <div className="text-fg-mute">Brand</div>
              <div className="text-fg">{summary.brandName ?? "—"}</div>
              <div className="text-fg-mute">Umbrella brand</div>
              <div className="text-fg">
                {summary.umbrellaBrand
                  ? summary.umbrellaBrand
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())
                  : "—"}
              </div>
              <div className="text-fg-mute">Total rooms</div>
              <div className="font-mono text-fg">
                {summary.totalRooms ?? "—"}
              </div>
              {summary.vendor?.toLowerCase().includes("meraki") ? (
                <>
                  <div className="text-fg-mute">Network ID</div>
                  <div className="flex min-w-0 items-center gap-1.5 font-mono text-fg">
                    <span className="truncate">{summary.networkId ?? "—"}</span>
                    {summary.networkId ? (
                      <button
                        type="button"
                        onClick={() =>
                          p.copyPropertyField(summary.networkId, "networkId")
                        }
                        className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-line text-fg-mute transition-colors hover:bg-surface-2 hover:text-fg"
                        aria-label="Copy network ID"
                        title="Copy network ID"
                      >
                        {p.copiedField === "networkId" ? (
                          <Check size={11} />
                        ) : (
                          <Copy size={11} />
                        )}
                      </button>
                    ) : null}
                  </div>
                </>
              ) : null}
              <div className="text-fg-mute">Timezone</div>
              <div className="text-fg">
                {(summary.timezoneName ?? "—").replace(/_/g, " ") +
                  ` (${gmtOffsetLabel(summary.timezoneOffsetMinutes)})`}
              </div>
              <div className="text-fg-mute">Navision</div>
              <div className="font-mono text-fg">
                {summary.navisionCode ?? "—"}
              </div>
              <div className="text-fg-mute">Address</div>
              <div className="flex min-w-0 items-center gap-1.5 text-fg">
                <span className="truncate">{summary.address ?? "—"}</span>
                {summary.address ? (
                  <button
                    type="button"
                    onClick={() =>
                      p.copyPropertyField(summary.address, "address")
                    }
                    className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-line text-fg-mute transition-colors hover:bg-surface-2 hover:text-fg"
                    aria-label="Copy address"
                    title="Copy address"
                  >
                    {p.copiedField === "address" ? (
                      <Check size={11} />
                    ) : (
                      <Copy size={11} />
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <DashboardAiSummaryPanel
          showResummarize={p.showResummarize}
          summarizeReady={p.summarizeReady}
          summarizing={p.summarizing}
          gatheringSummaryData={p.gatheringSummaryData}
          computingSummary={p.computingSummary}
          summaryError={p.summaryError}
          summaryData={p.summaryData}
          onSummarize={(force) => {
            void p.summarizeDashboard(force);
          }}
        />
      </div>
    </>
  );
}

function SiteDashboardThroughputSection({ p, chartExportRef }: { p: SiteOverviewPageState; chartExportRef: React.RefObject<HTMLDivElement> }) {
  const summary = p.summary!;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px]">
      {p.showThroughputSkeleton ? (
        <PanelSkeleton />
      ) : (
        <Card>
          <CardContent>
            <div className="mb-3 flex flex-col gap-2">
              <div>
                <CardTitle>Throughput</CardTitle>
                <CardSub>
                  {p.throughputGraphLabel} · {rangeLabel(p.range)} · UTC
                  {fmtFetchedAt(p.sectionFetchedAt.throughput) ? (
                    <> · {fmtFetchedAt(p.sectionFetchedAt.throughput)}</>
                  ) : null}
                </CardSub>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={p.chartData.length === 0}
                    >
                      <Download size={12} className="text-fg-dim" />
                      Download
                      <ChevronDown size={11} className="text-fg-mute" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onSelect={() => p.downloadThroughputCsv(summary.code)}
                      disabled={
                        p.chartData.length === 0 || p.activeSeries.length === 0
                      }
                    >
                      <FileSpreadsheet
                        size={12}
                        className="mr-1 text-fg-dim"
                      />
                      CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() =>
                        void p.downloadThroughputPdf(summary.code)
                      }
                      disabled={p.chartData.length === 0 || p.exportingPdf}
                    >
                      <FileText size={12} className="mr-1 text-fg-dim" />
                      {p.exportingPdf ? "Generating PDF" : "PDF"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void p.refreshSection("throughput")}
                  disabled={p.sectionLoading("throughput")}
                  title="Refresh throughput section"
                >
                  <RefreshCw
                    size={12}
                    className={cn(
                      "text-fg-dim",
                      p.sectionLoading("throughput") && "animate-spin",
                    )}
                  />
                  Refresh
                </Button>
                {p.hasThroughputLimits ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      p.setShowThroughputPercent((current) => !current)
                    }
                  >
                    {p.showThroughputPercent ? "Show b/s" : "Show %"}
                  </Button>
                ) : null}
                {p.hasOveruse ? (
                  <Badge
                    variant="warn"
                    title={`${p.overuseConsecutiveDays} consecutive day${p.overuseConsecutiveDays === 1 ? "" : "s"} over limit in this range`}
                  >
                    <AlertTriangle size={12} />
                    Overuse detected
                  </Badge>
                ) : null}
              </div>
            </div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <Tabs
                value={p.throughputGraph}
                onValueChange={(value) =>
                  p.setThroughputGraph(value as ThroughputGraph)
                }
              >
                <TabsList>
                  <TabsTrigger value="applianceUplinkBandwidth">
                    Appliance Uplink Bandwidth
                  </TabsTrigger>
                  <TabsTrigger value="clientBandwidthUsage">
                    Client Bandwidth Usage
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="flex items-center gap-1 text-xs">
                <button
                  type="button"
                  onClick={() => p.toggleThroughputSeries("Received")}
                  className={cn(
                    "rounded px-1.5 py-0.5 font-mono text-xs transition-colors",
                    p.visibleThroughputSeries.Received
                      ? "text-cyan"
                      : "text-fg-mute hover:text-fg-dim",
                  )}
                >
                  Received
                </button>
                <span className="text-fg-mute/70">|</span>
                <button
                  type="button"
                  onClick={() => p.toggleThroughputSeries("Sent")}
                  className={cn(
                    "rounded px-1.5 py-0.5 font-mono text-xs transition-colors",
                    p.visibleThroughputSeries.Sent
                      ? "text-peach"
                      : "text-fg-mute hover:text-fg-dim",
                  )}
                >
                  Sent
                </button>
                {p.hasThroughputLimits ? (
                  <>
                    <span className="text-fg-mute/70">|</span>
                    <button
                      type="button"
                      onClick={() => {
                        const next = !p.showBandwidthLimits;
                        p.setShowRxLimitLine(next);
                        p.setShowTxLimitLine(next);
                      }}
                      className={cn(
                        "rounded px-1.5 py-0.5 font-mono text-xs transition-colors",
                        p.showBandwidthLimits
                          ? "text-rose"
                          : "text-fg-mute hover:text-fg-dim",
                      )}
                    >
                      Bandwidth Limits
                    </button>
                  </>
                ) : null}
              </div>
            </div>
            <div ref={chartExportRef} style={{ width: "100%", height: 240 }}>
              <ThroughputChartSurface
                  idPrefix="throughput-live"
                  data={p.chartData}
                  yAxisMax={Math.max(
                    p.yAxisMax,
                    p.showBandwidthLimits && p.percentageLimitBase > 0
                      ? Math.ceil(p.percentageLimitBase * 1.1)
                      : 1,
                  )}
                  visibleSeries={p.visibleThroughputSeries}
                  rxLimitBps={p.showRxLimit ? p.downLimitBps : 0}
                  txLimitBps={p.showTxLimit ? p.upLimitBps : 0}
                  sharedLimitBps={
                    p.showBandwidthLimits && p.percentageLimitBase > 0
                      ? p.percentageLimitBase
                      : 0
                  }
                  valueFormatter={p.throughputValueFormatter}
                />
            </div>
            <div className="mt-3 flex flex-col gap-2 text-[11px] text-fg-mute">
              <ChartStat
                color="bg-cyan"
                label="Downstream"
                avg={p.rxAvg}
                max={p.rxMax}
                min={p.rxMin}
                isMuted={!p.visibleThroughputSeries.Received}
                limit={p.downLimitBps}
                showPercent={p.showThroughputPercent}
              />
              <ChartStat
                color="bg-peach"
                label="Upstream"
                avg={p.txAvg}
                max={p.txMax}
                min={p.txMin}
                isMuted={!p.visibleThroughputSeries.Sent}
                limit={p.upLimitBps}
                showPercent={p.showThroughputPercent}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {p.showWanSkeleton ? (
        <PanelSkeleton />
      ) : (
        <ApplianceStatusRail
          uplinks={p.applianceStatusCards}
          siteCode={summary.code}
          friendlyName={summary.friendlyName}
        />
      )}
    </div>
  );
}

function SiteDashboardNetworkSection({ p, networkDevicesBodyRef }: { p: SiteOverviewPageState; networkDevicesBodyRef: ReturnType<typeof useUiAutoAnimate> }) {
  return (
    <Card className="overflow-visible">
      <CardContent className="flex flex-col gap-3 overflow-visible">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h2 className="m-0 text-base font-bold text-fg">
              Network devices
            </h2>
            <p className="mt-0.5 text-xs text-fg-mute">
              Switch between topology, diagram, and table views of the same
              filtered device data.
              {fmtFetchedAt(p.sectionFetchedAt.devices) ? (
                <span className="ml-1 opacity-70">
                  · {fmtFetchedAt(p.sectionFetchedAt.devices)}
                </span>
              ) : null}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto lg:items-center">
            <div className="flex items-center gap-2 px-1 py-1">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-mute">
                View
              </span>
              <Button
                size="sm"
                variant={
                  p.networkDevicesView === "topology" ? "primary" : "ghost"
                }
                onClick={() => p.setNetworkDevicesView("topology")}
              >
                Topology
              </Button>
              <Button
                size="sm"
                variant={
                  p.networkDevicesView === "diagram" ? "primary" : "ghost"
                }
                onClick={() => p.setNetworkDevicesView("diagram")}
              >
                Diagram
              </Button>
              <Button
                size="sm"
                variant={p.networkDevicesView === "table" ? "primary" : "ghost"}
                onClick={() => p.setNetworkDevicesView("table")}
              >
                Table
              </Button>
            </div>
            <span className="hidden h-7 w-px bg-line/80 sm:block" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="md"
                  variant="toolbar"
                  disabled={p.networkDevicesExportRows.length === 0}
                  className="w-full sm:w-auto"
                >
                  <Download size={12} className="text-fg-dim" />
                  Download
                  <ChevronDown size={11} className="text-fg-mute" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={p.downloadNetworkDevicesCsv}>
                  <FileText size={12} className="mr-1 text-fg-dim" />
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => void p.downloadNetworkDevicesXlsx()}
                  disabled={p.exportingDevicesXlsx}
                >
                  <FileSpreadsheet size={12} className="mr-1 text-fg-dim" />
                  {p.exportingDevicesXlsx ? "Generating XLSX..." : "XLSX"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="md"
              variant="toolbar"
              onClick={() => void p.refreshSection(p.activeNetworkSection)}
              disabled={p.sectionLoading(p.activeNetworkSection)}
              title="Refresh network devices section"
              className="w-full sm:w-auto"
            >
              <RefreshCw
                size={12}
                className={cn(
                  "text-fg-dim",
                  p.sectionLoading(p.activeNetworkSection) && "animate-spin",
                )}
              />
              Refresh
            </Button>
          </div>
        </div>

        <div ref={networkDevicesBodyRef} className="flex flex-col gap-3">
          {p.showDevicesSkeleton ? (
            <PanelSkeleton />
          ) : p.networkDevicesView === "table" ? (
            <DevicesTable
              embedded
              devices={p.filteredDevices}
              enableExport={false}
              timeAgo={timeAgo}
            />
          ) : (
            <>
              <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative w-full sm:w-[320px]">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
                  />
                  <Input
                    value={p.topologyQuery}
                    onChange={(e) => p.setTopologyQuery(e.target.value)}
                    placeholder="Search name, serial, or MAC"
                    className="pl-9"
                  />
                </div>
                <MultiSelectDropdown
                  triggerLabel="Device types"
                  options={DEVICE_CLASS_OPTIONS}
                  selected={Array.from(p.selectedDeviceClasses)}
                  onChange={(next) =>
                    p.setSelectedDeviceClasses(
                      new Set(next as Array<TopologyDeviceClass>),
                    )
                  }
                  noneSelectedLabel="All"
                  className="w-full sm:w-[220px]"
                />
                <Input
                  value={p.tagFilter}
                  onChange={(e) => p.setTagFilter(e.target.value)}
                  placeholder="Filter by tag"
                  className="w-full sm:w-[220px]"
                />
              </div>
              {p.networkDevicesView === "diagram" ? (
                <SiteDiagram
                  key={p.slug}
                  devices={p.filteredDevices}
                  embedded
                  hideTitle
                  statusCounts={p.statusCounts}
                  selectedStatuses={p.selectedStatuses}
                  onToggleStatus={p.handleToggleStatus}
                  onResetStatuses={p.handleResetStatuses}
                />
              ) : (
                <TopologyExplorer
                  nodes={p.topologyLayout.nodes}
                  edges={p.topologyLayout.edges}
                  colorMode={p.themeMode}
                  fitDep={`dashboard-devices-${p.slug}-${p.topologyLayout.mode}-${p.topologyLayout.nodes.length}`}
                  initialFocusNodeId={p.topologyLayout.focusNodeId ?? undefined}
                  height={520}
                  emptyMessage="No devices match current filters."
                  filterLabel="Filters"
                  showAccessPointsByDefault={true}
                  getNodeStatus={(node) => {
                    const device = (
                      node.data as { device?: TopologyDevice } | undefined
                    )?.device;
                    if (!device) return "unknown";
                    if (device.status === "dormant") return "unknown";
                    return device.status;
                  }}
                  isAccessPointNode={(node) => {
                    const device = (
                      node.data as { device?: TopologyDevice } | undefined
                    )?.device;
                    return device?.deviceClass === "wireless";
                  }}
                  onSelectedNodeChange={(node) => {
                    if (!node) return;
                    if (node.id.startsWith(AP_GROUP_PREFIX)) {
                      p.setExpandedApGroups((prev) => {
                        const next = new Set(prev);
                        next.add(node.id);
                        return next;
                      });
                      return;
                    }
                    const serial = p.serialFromTopologyNode(node);
                    if (!serial) return;
                    void p.loadTopologyDetails(serial);
                  }}
                  onRefreshSelectedNode={(node) => {
                    const serial = p.serialFromTopologyNode(node);
                    if (!serial) return;
                    void p.loadTopologyDetails(serial, { force: true });
                  }}
                  getNodeDetails={(node) => {
                    const serial = p.serialFromTopologyNode(node);
                    if (!serial) return undefined;
                    const details = p.topologyDetailsBySerial[serial];
                    if (!details) return undefined;
                    return {
                      serial: details.serial,
                      name: details.name,
                      model: details.model,
                      productType: details.productType,
                      ports: details.ports,
                      clients: details.clients,
                    };
                  }}
                  getNodeDetailsLoading={(node) => {
                    const serial = p.serialFromTopologyNode(node);
                    if (!serial) return false;
                    return !!p.topologyDetailsLoading[serial];
                  }}
                  getNodeDetailsError={(node) => {
                    const serial = p.serialFromTopologyNode(node);
                    if (!serial) return undefined;
                    return p.topologyDetailsErrors[serial];
                  }}
                  renderNodeDetails={(node) => {
                    if (node.id === ISP_NODE_ID) {
                      return (
                        <div className="rounded-md border border-line bg-surface-2/50 px-2 py-2 text-[11px] text-fg-dim">
                          Synthetic WAN root. Connected upstream internet
                          path.
                        </div>
                      );
                    }
                    if (node.id.startsWith(AP_GROUP_PREFIX)) {
                      const count =
                        ((node.data as { memberCount?: number } | undefined)
                          ?.memberCount as number | undefined) ?? 0;
                      return (
                        <div className="rounded-md border border-violet/30 bg-aurora-soft px-2 py-2 text-[11px] text-violet">
                          AP group with {count} access points. Click node to
                          expand.
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function SiteOverviewPage() {
  const animatedRootRef = useUiAutoAnimate();
  const chartExportRef = useRef<HTMLDivElement>(null);
  const networkDevicesBodyRef = useUiAutoAnimate();
  const p = useSiteOverviewPage(chartExportRef);

  if (!p.summary) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <CardGridSkeleton cards={4} />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.3fr_0.7fr]">
          <PanelSkeleton />
          <PanelSkeleton />
        </div>
        <TableSkeleton columns="1.2fr 1fr 0.7fr 1fr" columnCount={4} rows={5} />
      </div>
    );
  }

  const summary = p.summary;

  return (
    <m.div
      ref={animatedRootRef}
      className="flex flex-col gap-6 pb-28"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/sites" className="text-fg-dim hover:text-fg">
          Sites
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">{summary.code}</span>
      </div>

      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Site Overview
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Live throughput, appliance status, and network topology for any
            Meraki site your API key sees.
          </p>
        </div>
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          <Button asChild size="sm" variant="ghost">
            <Link to="/sites">
              <ArrowLeft size={13} />
              Back to Site List
            </Link>
          </Button>
        </div>
      </div>

      <NoVendorKeysBanner vendor="meraki" />

      {p.error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{p.error}</span>
          </span>
        </output>
      ) : null}

      {summary.synapseWarning ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>{summary.synapseWarning}</span>
        </output>
      ) : null}

      <SiteDashboardHeaderSection p={p} />

      {/* Three device-class cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {p.showRollupsSkeleton ? (
          <CardGridSkeleton cards={3} />
        ) : (
          p.filteredRollups.map((r) => (
            <DeviceClassCard key={r.label} rollup={r} />
          ))
        )}
      </div>

      {/* Three stat tiles */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatTile
          label="WAN Uplinks"
          icon={<Cable size={14} />}
          value={`${summary.uplinks.active} / ${summary.uplinks.total}`}
          hint={
            summary.uplinks.active === summary.uplinks.total
              ? "all active"
              : "see status"
          }
        />
        <StatTile
          label="Active Clients"
          icon={<Users size={14} />}
          numericValue={summary.activeClients}
          formatFn={(v) => Math.round(v).toLocaleString()}
          hint={`${summary.heavyClientsLastHour} heavy users`}
        />
        <StatTile
          label="Peak Throughput"
          icon={<Activity size={14} />}
          numericValue={p.peak}
          formatFn={fmtBps}
          hint="rx + tx, in range"
        />
      </div>

      <SiteDashboardThroughputSection p={p} chartExportRef={chartExportRef} />

      <SiteDashboardNetworkSection p={p} networkDevicesBodyRef={networkDevicesBodyRef} />

      {p.showFirmwareSkeleton ? (
        <PanelSkeleton />
      ) : (
        <FirmwareUpgradesSection
          upgrades={p.firmwareUpgrades}
          range={p.range}
          refreshing={p.sectionLoading("firmware")}
          onRefresh={() => void p.refreshSection("firmware")}
          rangeOptions={RANGE_OPTIONS}
          isWithinRange={isWithinRange}
          fmtDateTime={fmtDateTime}
          dayKeyForTimezone={dayKeyForTimezone}
          dayLabelForTimezone={dayLabelForTimezone}
          exportRowsCsv={exportRowsCsv}
          exportRowsXlsx={exportRowsXlsx}
        />
      )}

      {p.licenses.length > 0 ? (
        p.showLicensesSkeleton ? (
          <PanelSkeleton />
        ) : (
          <LicensesSection
            licenses={p.licenses}
            refreshing={p.sectionLoading("licenses")}
            onRefresh={() => void p.refreshSection("licenses")}
            fmtDateTime={fmtDateTime}
          />
        )
      ) : null}

      {p.showAlertsSkeleton ? (
        <PanelSkeleton />
      ) : (
        <AlertsSection
          alerts={p.alertsHistory}
          assuranceAlerts={p.assuranceAlerts}
          wirelessFailedConnections={p.wirelessFailedConnections}
          activeTab={p.alertsTab}
          onTabChange={p.setAlertsTab}
          range={p.range}
          loadingNetworkEvents={p.sectionLoading("alerts")}
          loadingHealth={p.showAssuranceSkeleton}
          loadingConnections={p.showConnectionsSkeleton}
          onRefreshNetworkEvents={() => void p.refreshSection("alerts")}
          onRefreshHealth={() => void p.refreshSection("assuranceAlerts")}
          onRefreshConnections={() =>
            void p.refreshSection("wirelessFailedConnections")
          }
          rangeLabel={rangeLabel}
          isWithinRange={isWithinRange}
          fmtDateTime={fmtDateTime}
        />
      )}

      {p.showClientsSkeleton ? (
        <TableSkeleton columns="1.2fr 1fr 0.7fr 1fr" columnCount={4} rows={5} />
      ) : (
        <ConnectedClientsTable
          clients={p.clients}
          totalShown={summary.activeClients}
          range={p.range}
          refreshing={p.sectionLoading("clients")}
          onRefresh={() => void p.refreshSection("clients")}
          rangeLabel={rangeLabel}
          isWithinRange={isWithinRange}
          timeAgo={timeAgo}
          fmtBytes={fmtBytes}
          exportRowsCsv={exportRowsCsv}
          exportRowsXlsx={exportRowsXlsx}
        />
      )}
    </m.div>
  );
}
